<template>
  <div>
    <div class="mt-6">
      <div class="subtitle1">{{ $t(`modals.${SecurityAwarenessAction.EDIT_USERS}.service`) }}</div>

      <div class="subtitle2 d-flex align-center">
        <v-icon size="24" v-if="isCustomDomain(service)" icon="$cube"></v-icon>
        <coro-icon v-else class="service-icon" :icon-name="service"></coro-icon>
        {{ isCustomDomain(service) ? service : $t(`services.${service}`) }}
      </div>
    </div>

    <v-divider class="mt-6 mb-6"></v-divider>

    <div class="subtitle1">{{ $t("modals.allowCloudService.step4.title") }}</div>
    <div class="body2">{{ $t("modals.allowCloudService.step4.subtitle") }}</div>

    <assign-users
      @model-change="onAssignUserChange"
      :groups="localValue.groups"
      :users="localValue.users"
      :labels="localValue.labels"
      :user-type="localValue.userType"
      :hideGroups="isOtherProvider"
      :service="localValue.selectedService"
    ></assign-users>
  </div>
</template>

<script lang="ts">
import { type PropType, computed, defineComponent, ref } from "vue";
import { AccessPermissionType, Service } from "@/constants/cloud-apps.ts";
import CoroIcon from "@/components/CoroIcon.vue";
import AssignUsers, {
  type AssignUsersForm,
} from "@/components/modals/security-awareness/AssignUsers.vue";
import type { DialogDataConfig } from "@/_store/dialogs.module.ts";
import type { SatConnection } from "@/_store/security-awareness/security-awareness.adapters.ts";
import { SecurityAwarenessAction } from "@/constants/security-awareness.ts";

export default defineComponent({
  components: { CoroIcon, AssignUsers },
  props: {
    config: {
      type: Object as PropType<DialogDataConfig<SatConnection>>,
      required: true,
    },
    disable: {
      type: Boolean,
      required: true,
    },
  },
  emits: ["update:localValue", "dismissed"],
  setup(props, { emit }) {
    const service = ref(props.config.item.name);

    const isCustomDomain = (name: string | Service) => {
      return !Object.values(Service).includes(name as Service);
    };

    const localValue = ref({
      selectedService: isCustomDomain(service.value) ? "other" : service.value,
      userType: props.config.item.userType || AccessPermissionType.ALL_USERS,
      groups: props.config.item.groups || [],
      users: props.config.item.users || [],
      labels: props.config.item.labels || [],
      includeUsers: !!props.config.item.users?.length,
      includeGroups: !!props.config.item.groups?.length,
      includeLabels: !!props.config.item.labels?.length,
    });

    emit("update:localValue", localValue.value);

    const isOtherProvider = computed(() => {
      return localValue.value.selectedService === "other";
    });

    const onAssignUserChange = (newValue: AssignUsersForm) => {
      const users =
        localValue.value.userType === AccessPermissionType.ALL_USERS
          ? {
              groups: [],
              users: [],
              labels: [],
            }
          : {
              groups: newValue.groups,
              users: newValue.users,
              labels: newValue.labels,
            };

      localValue.value = {
        ...localValue.value,
        ...newValue,
        ...users,
      };

      emit("update:localValue", localValue.value);
    };

    return {
      service,
      localValue,
      isOtherProvider,
      onAssignUserChange,
      isCustomDomain,
      SecurityAwarenessAction,
    };
  },
});
</script>

<style lang="scss" scoped>
:deep(.v-stepper-vertical__item:not(:last-child)) {
  .v-stepper-vertical__item:not(:last-child)::after {
    border-left: 1px solid rgba(var(--v-theme-on-surface));
  }
}

:deep(*) {
  .icon-check-xs:before {
    color: rgba(var(--v-theme-white));
  }

  .text-indigo-medium {
    .icon-event-detail-s:before {
      color: rgb(var(--v-theme-indigo-medium));
    }
  }

  .v-stepper-vertical-item__avatar.v-avatar {
    background: rgb(var(--v-theme-indigo-pale));
    color: rgb(var(--v-theme-primary));
  }

  .v-expansion-panel--active .v-stepper-vertical-item__avatar.v-avatar {
    background: rgb(var(--v-theme-indigo-medium));
    color: rgb(var(--v-theme-white));
  }

  .step-completed {
    .v-avatar {
      background: rgb(var(--v-theme-success)) !important;
    }
  }

  .step-error {
    .v-avatar {
      background: rgb(var(--v-theme-red-dark)) !important;
    }

    .step-title {
      color: rgb(var(--v-theme-red-dark)) !important;
    }
  }

  .info-alert .icon:before {
    color: rgb(var(--v-theme-primary)) !important;
  }
}

.step1 {
  .v-chip {
    width: 200px;
    height: 160px !important;
    border-radius: 6px;
    background-color: rgb(var(--v-theme-white));
    border: 0;
    justify-content: center;

    .v-chip__content {
      width: 100% !important;
      text-align: center;
    }
  }

  .v-chip:not(.v-chip--selected) {
    border: solid 1px rgb(var(--v-theme-indigo-pale));
  }

  .v-chip-group .v-chip--selected {
    border: 2px solid rgb(var(--v-theme-primary)) !important;
  }
}

.info-alert {
  background-color: rgb(var(--v-theme-indigo-pale));
}

ul {
  list-style-type: disc !important;
}

.text-red-dark::before {
  color: rgb(var(--v-theme-red-dark));
}

.service-icon {
  height: 24px;
  width: 24px;
}
</style>
